import { AdminStatusDto, CandidateStatusCountsDto, CandidateStatusDto } from '@/api/generated';

export type QuickFilter =
  'afterQuiz' | 'all' | 'fromFair' | 'inProgress' | 'negative' | 'onHold' | 'positive' | 'stopped';

interface QuickFilterCriteria {
  adminStatuses?: AdminStatusDto[];
  isFromFair?: boolean;
  statuses?: CandidateStatusDto[];
}

// The statuses of the admin panel grouped the way recruiters think about candidates
export const QUICK_FILTERS: Record<QuickFilter, QuickFilterCriteria> = {
  afterQuiz: {
    statuses: [
      CandidateStatusDto.QuizFinished,
      CandidateStatusDto.QuizPointsResultReady,
      CandidateStatusDto.CandidateProcessFinished,
    ],
  },
  all: {},
  fromFair: { isFromFair: true },
  inProgress: {
    statuses: [
      CandidateStatusDto.EmailProvided,
      CandidateStatusDto.EmailVerified,
      CandidateStatusDto.PersonalDataProvided,
      CandidateStatusDto.InQuiz,
    ],
  },
  negative: {
    statuses: [CandidateStatusDto.NegativeFeedback, CandidateStatusDto.NegativeFeedbackSent],
  },
  onHold: { adminStatuses: [AdminStatusDto.OnHoldAdmin, AdminStatusDto.OnHoldConditional] },
  positive: {
    statuses: [
      CandidateStatusDto.PositiveFeedback,
      CandidateStatusDto.SentToERecruiter,
      CandidateStatusDto.PositiveFeedbackSent,
    ],
  },
  stopped: {
    statuses: [CandidateStatusDto.QuizExpired, CandidateStatusDto.QuizRevisitsExceeded],
  },
};

export const QUICK_FILTER_ORDER: QuickFilter[] = [
  'all',
  'inProgress',
  'afterQuiz',
  'onHold',
  'positive',
  'negative',
  'stopped',
  'fromFair',
];

export const countFor = (
  filter: QuickFilter,
  counts: CandidateStatusCountsDto | undefined,
): number => {
  if (!counts) {
    return 0;
  }
  const criteria = QUICK_FILTERS[filter];
  if (filter === 'all') {
    return counts.total;
  }
  if (criteria.isFromFair) {
    return counts.fromFair;
  }
  if (criteria.adminStatuses) {
    return counts.adminStatuses
      .filter(count => criteria.adminStatuses?.includes(count.adminStatus))
      .reduce((sum, count) => sum + count.count, 0);
  }
  return counts.statuses
    .filter(count => criteria.statuses?.includes(count.status))
    .reduce((sum, count) => sum + count.count, 0);
};

export type StatusTone = 'critical' | 'good' | 'neutral' | 'pending' | 'stopped';

export const statusTone = (status: CandidateStatusDto): StatusTone => {
  if (QUICK_FILTERS.positive.statuses?.includes(status)) {
    return 'good';
  }
  if (QUICK_FILTERS.negative.statuses?.includes(status)) {
    return 'critical';
  }
  if (QUICK_FILTERS.afterQuiz.statuses?.includes(status)) {
    return 'pending';
  }
  if (QUICK_FILTERS.stopped.statuses?.includes(status)) {
    return 'stopped';
  }
  return 'neutral';
};

// Status colors of the dataviz palette (good / critical) and neutral grays, always shown with an icon and the label
export const TONE_STYLES: Record<StatusTone, { background: string; color: string }> = {
  critical: { background: '#fbe9e9', color: '#a52a2a' },
  good: { background: '#e6f4e6', color: '#006300' },
  neutral: { background: '#eef1f5', color: '#3a4a5c' },
  pending: { background: '#fff1e3', color: '#8a3c00' },
  stopped: { background: '#f1f1f1', color: '#595959' },
};

export interface SearchCriteria {
  email?: string;
  lastName?: string;
  phone?: string;
}

/**
 * One search box for the three text filters: an e-mail has an @, a phone number is mostly digits
 */
export const parseSearch = (text: string): SearchCriteria => {
  const value = text.trim();
  if (!value) {
    return {};
  }
  if (value.includes('@')) {
    return { email: value };
  }
  const digits = value.replace(/\D/g, '');
  if (digits.length >= 3 && digits.length >= value.replace(/[\s+()-]/g, '').length) {
    return { phone: digits };
  }
  return { lastName: value };
};

/**
 * Some dates of the candidates list come as UTC without a zone (e.g. 2026-10-11T11:55:00): read them as UTC,
 * not as local time
 */
export const parseUtc = (date: string | undefined): Date | undefined => {
  if (!date) {
    return undefined;
  }
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(date);
  return new Date(hasZone ? date : `${date}Z`);
};

/**
 * @return e.g. "2 d 4 h", "5 h", "40 min"; undefined when the moment has passed
 */
export const timeLeft = (until: Date, now: Date): string | undefined => {
  const minutes = Math.floor((until.getTime() - now.getTime()) / 60_000);
  if (minutes < 0) {
    return undefined;
  }
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) {
    return `${days} d ${hours} h`;
  }
  if (hours > 0) {
    return `${hours} h`;
  }
  return `${minutes} min`;
};

export const initials = (firstName: string | undefined, lastName: string | undefined): string =>
  `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
